import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { StatStrip, type StatItem } from "@/components/ui/page-header";
import { Icon } from "@/lib/icons";
import { TaskHeader } from "./TaskHeader";
import type { ContributionTaskKey } from "./task-registry";
import { useKitCopy } from "./use-kit-copy";

/**
 * The screen before a session: what the task is, the choices it needs
 * (language, collection place...), its rules and the start button.
 *
 * ```tsx
 * <TaskSetup
 *   task="speak"
 *   title={t.speakContributeTitle}
 *   description={t.speakContributeSubtitle}
 *   stats={[{ label: t.phrasesToRead, value: "5" }]}
 *   instructions={[t.speakInstruction1, t.speakInstruction2]}
 *   startLabel={t.startSession}
 *   onStart={start}
 *   startDisabled={!language}
 *   starting={isStarting}
 * >
 *   <CollectionPlaceStep state={place} />
 *   <TaskLanguageSelect ... />
 * </TaskSetup>
 * ```
 *
 * - `children`: the form fields, in order.
 * - `notices`: banners above the form (resumed session, validation gate).
 * - `onBack`: defaults to the contributor home.
 * - `startTestId`: data-testid of the start button, for browser tests.
 */
export function TaskSetup({
  task,
  title,
  description,
  stats = [],
  instructions = [],
  notices,
  children,
  startLabel,
  onStart,
  startDisabled = false,
  starting = false,
  onBack,
  startTestId,
}: {
  task: ContributionTaskKey;
  title: ReactNode;
  description?: ReactNode;
  stats?: StatItem[];
  instructions?: string[];
  notices?: ReactNode;
  children?: ReactNode;
  startLabel: string;
  onStart: () => void;
  startDisabled?: boolean;
  starting?: boolean;
  onBack?: () => void;
  startTestId?: string;
}) {
  const { copy } = useKitCopy();
  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 pb-12 pt-5 sm:px-6 sm:pt-6">
      <TaskHeader task={task} onBack={onBack} />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:items-start">
        <section className="space-y-4">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">{title}</h1>
            {description ? <p className="max-w-xl text-sm leading-6 text-muted-foreground sm:text-base">{description}</p> : null}
          </div>
          {stats.length > 0 ? <StatStrip items={stats} className="max-w-md" /> : null}
          {instructions.length > 0 ? (
            <div className="space-y-2 pt-2">
              <h2 className="text-sm font-medium text-foreground">{copy.setupInstructions}</h2>
              <ul className="space-y-2 text-sm leading-6 text-muted-foreground">
                {instructions.map((line) => (
                  <li key={line} className="flex gap-3">
                    <Icon name="check" className="mt-1 size-4 shrink-0 text-brand" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>

        <section className="space-y-5 rounded-xl border border-border/60 bg-surface p-5 sm:p-6">
          {notices ? <div className="space-y-3">{notices}</div> : null}
          {children}
          <Button className="h-12 w-full" onClick={onStart} disabled={startDisabled || starting} data-testid={startTestId}>
            {startLabel}
            {starting ? <Icon name="loader2" className="animate-spin" /> : <Icon name="arrowRight" />}
          </Button>
        </section>
      </div>
    </div>
  );
}
